 import './Dropdown.scss';
import React, { memo, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Helpers from '../../js/helpers';

type DropdownProps = {
    autoComplete?: string;
    className?: string;
    disabled?: boolean;
    fieldDescription?: string;
    index?: number;
    isValid?: boolean;
    isValidating?: boolean;
    label: string;
    maxOptionsLength?: number;
    name: string;
    notActive?: boolean;
    onBlurHandler?: (e: React.MouseEvent<HTMLLIElement>) => void;
    onFocusHandler?: (e: React.MouseEvent<HTMLLIElement>) => void;
    onMouseEnterHandler?: (e: React.MouseEvent<HTMLLIElement>) => void;
    onMouseLeave?: (e: React.MouseEvent<HTMLLIElement>) => void;
    onSelect?: (
        e: React.MouseEvent<HTMLLIElement>,
        name: string,
        option: string,
        nextIndex: number
    ) => void;
    onTextChange?: (e: React.MouseEvent<HTMLLIElement>) => void;
    options: string[];
    optionsLengthWarning?: string;
    preselection: number;
};

const Dropdown = ({
    autoComplete = 'off',
    className = '',
    disabled = false,
    fieldDescription,
    index = 0,
    isValid = true,
    isValidating = false,
    label,
    maxOptionsLength = 300,
    name,
    notActive,
    onBlurHandler = () => null,
    onFocusHandler = () => null,
    onMouseEnterHandler = () => null,
    onMouseLeave = () => null,
    onSelect,
    onTextChange = () => null,
    options,
    optionsLengthWarning,
    preselection = -1
}: DropdownProps) => {
    /**
     * Options
     */
    const newIndexedOptions = useMemo(
        () =>
            options.map((option, newIndex) => ({
                index: newIndex,
                value: option
            })),
        [options]
    );
    const [indexedOptions, setIndexedOptions] = useState(newIndexedOptions);
    const [filteredOptions, setFilteredOptions] = useState(newIndexedOptions);

    /**
     * Values
     */
    const [value, setValue] = useState(
        preselection !== -1 ? newIndexedOptions[preselection].value : ''
    );
    const [selectionIndex, setSelectionIndex] = useState(preselection);
    const [currentIndex, setCurrentIndex] = useState(-1);

    const [hasFocus, setHasFocus] = useState(false);

    useEffect(() => {
        const newIndexes = options.map((option, newIndex) => ({
            index: newIndex,
            value: option
        }));
        setIndexedOptions(newIndexes);
        setValue(
            typeof preselection === 'undefined' || preselection === -1
                ? ''
                : newIndexes[preselection].value
        );

        setFilteredOptions(newIndexes);
    }, [options]);

    useEffect(() => {
        setSelectionIndex(
            typeof preselection !== 'undefined' && preselection !== -1
                ? preselection
                : selectionIndex
        );

        setValue(
            typeof preselection === 'undefined' || preselection === -1
                ? ''
                : indexedOptions[preselection].value
        );
    }, [preselection]);

    const onFocus = e => {
        if (onFocusHandler) {
            onFocusHandler(e);
        }
        setHasFocus(true);
    };

    const onBlur = e => {
        setHasFocus(false);
        setFilteredOptions(indexedOptions);
        /**
         * Checks if the value exists with given selectionIndex
         * For example in the case of ListBuilder where options are removed from the list
         */
        setValue(
            selectionIndex === -1
                ? ''
                : indexedOptions[selectionIndex].value
                ? indexedOptions[selectionIndex].value
                : value
        );

        if (onBlurHandler) {
            onBlurHandler(e);
        }
    };

    const onMouseEnter = e => {
        if (onMouseEnterHandler) {
            onMouseEnterHandler(e);
        }
    };

    const onKeyDown = e => {
        // eslint-disable-next-line no-param-reassign
        e = e || window.event;
        let nextIndex = currentIndex;
        setHasFocus(true);
        if (e.keyCode === 38) {
            if (filteredOptions.length !== options.length) {
                // is currentIndex in filteredOptions?
                const currentFilteredOptionIndex = filteredOptions.findIndex(
                    element => element.value === value
                );
                if (currentFilteredOptionIndex >= 0) {
                    // find the next index of options from the next index in filtered options
                    const nextFilteredIndex = Math.max(
                        currentFilteredOptionIndex - 1,
                        0
                    );
                    const nextFilteredOption =
                        filteredOptions[nextFilteredIndex];

                    nextIndex = options.findIndex(
                        element => nextFilteredOption.value === element
                    );
                } else {
                    // currentIndex is outside of filteredOptions and we move up the list, so select the last filtered option
                    nextIndex = options.findIndex(
                        element =>
                            element ===
                            filteredOptions[filteredOptions.length - 1].value
                    );
                }
            } else {
                // select the previous option
                nextIndex = Math.max(selectionIndex - 1, 0);
                setSelectionIndex(nextIndex);
            }

            setCurrentIndex(nextIndex);
            setValue(options[nextIndex]);
        } else if (e.keyCode === 40) {
            // If no list is shown simply select the next item in indexedOptions
            if (filteredOptions.length !== options.length) {
                // is currentIndex in filteredOptions?
                const currentFilteredOptionIndex = filteredOptions.findIndex(
                    element => element.value === value
                );
                if (currentFilteredOptionIndex >= 0) {
                    // find the next index of options from the next index in filtered options
                    const nextFilteredIndex = Math.min(
                        currentFilteredOptionIndex + 1,
                        filteredOptions.length - 1
                    );
                    const nextFilteredOption =
                        filteredOptions[nextFilteredIndex];

                    nextIndex = options.findIndex(
                        element => nextFilteredOption.value === element
                    );
                } else {
                    // currentIndex is outside of filteredOptions and we move down the list, so select the first filtered option
                    nextIndex = options.findIndex(
                        element => element === filteredOptions[0].value
                    );
                }
            } else {
                // select the next option
                nextIndex = Math.min(selectionIndex + 1, options.length - 1);
                setSelectionIndex(nextIndex);
            }

            setCurrentIndex(nextIndex);
            setValue(options[nextIndex]);
        } else if (e.keyCode === 13) {
            if (nextIndex < 0) {
                return;
            }
            setSelectionIndex(nextIndex);
            setValue(options[nextIndex]);
            setFilteredOptions(indexedOptions);
            setHasFocus(false);
            setCurrentIndex(-1);

            if (typeof onSelect !== 'undefined') {
                onSelect(e, name, options[nextIndex], nextIndex);
            }
        }

        // move scroll offset to next index
        const listItems = [
            ...e.target.nextSibling.nextSibling.nextSibling.children[0]
                .childNodes
        ];
        const selectedItem = listItems.find(
            element =>
                parseInt(element.getAttribute('data-index'), 10) === nextIndex
        );
        if (typeof selectedItem !== 'undefined') {
            selectedItem.parentElement.parentElement.scrollTo({
                top: selectedItem.offsetTop,
                behavior: 'smooth'
            });
        }
    };

    const handleMouseLeave = e => {
        if (onMouseLeave) {
            onMouseLeave(e);
        }
    };

    const onChange = e => {
        e.persist();

        if (onTextChange) {
            onTextChange(e);
        }

        const query = e.target.value;

        setValue(query);
        setCurrentIndex(-1);

        try {
             
            // check if query is valid
             
            let regex = new RegExp(
                query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                'i'
            );
            let matchingOptions = indexedOptions.filter(opt =>
                opt.value.match(regex)
            );

            // if the list is too long but there is an exact match we need to show it.
            if (
                matchingOptions.length > maxOptionsLength &&
                optionsLengthWarning
            ) {
                regex = new RegExp(
                    `^${query}$`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                    'i'
                );
                const exactOptions = indexedOptions.filter(opt =>
                    opt.value.match(regex)
                );
                if (exactOptions.length > 0) {
                    matchingOptions = exactOptions;
                }
            }

            setFilteredOptions(matchingOptions);
        } catch (error) {
            throw new Error(JSON.stringify(error));
        }
    };

    const handleSelection = event => {
        event.persist();

        const val = event.target.textContent;
        const idx = indexedOptions.findIndex(opt => opt.value === val);

        setSelectionIndex(idx);
        setValue(val);
        setFilteredOptions(indexedOptions);

        if (typeof onSelect !== 'undefined') {
            onSelect(event, name, val, idx);
        }
    };

    const classes = {
        'b-dropdown': true,
        'b-dropdown-container': true,
        'is-focused': hasFocus,
        'is-disabled': disabled || disabled || !options.length,
        'is-dirty': value !== '',
        'is-invalid': isValidating && !isValid,
        'is-valid': isValidating && isValid,
        'not-active': notActive
    };

    const dropdownClass = classNames(classes, className);

    const validation: { valid?: string; invalid?: string } = {};

    if (isValidating && isValid) {
        validation.valid = '';
    } else if (isValidating && !isValid) {
        validation.invalid = '';
    }

    const renderOptions = () =>
        filteredOptions.map((option, i) => (
            <li
                className={classNames({
                    'dropdown-option': true,
                    'is-hovered': option.index === currentIndex,
                    selected: option.index === selectionIndex
                })}
                title={name.trim().toLowerCase()}
                // @ts-ignore
                name={name.trim().toLowerCase()}
                key={Helpers.generateKey(i)}
                data-index={option.index}
                onClick={handleSelection}
                onMouseDown={handleSelection}
                onKeyDown={e => {
                    if (e.keyCode === 13) handleSelection(e);
                }}
                role="button"
                data-testid={`dropdown-option-${option.value}`}
            >
                {option.value}
            </li>
        ));

    return (
        <div className={dropdownClass}>
            <input
                data-testid={`dropdown-${name.trim().toLowerCase()}`}
                id={name.trim().toLowerCase()}
                name={name.trim().toLowerCase()}
                type="text"
                className={name.trim().toLowerCase()}
                value={value}
                onKeyDown={onKeyDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                disabled={disabled || !options.length}
                autoComplete={autoComplete}
                {...validation}
            />
            <label
                id={`${name.trim().toLowerCase()}-label`}
                htmlFor={name.trim().toLowerCase()}
            >
                {label}
            </label>
            <span className="focus-line" />
            {hasFocus && (
                <div className="b-dropdown-list">
                    <ul
                        className="b-dropdown__options"
                        // @ts-ignore
                        name={name.trim().toLowerCase()}
                    >
                        {(filteredOptions.length <= 300 ||
                            !optionsLengthWarning) &&
                            renderOptions()}
                    </ul>
                </div>
            )}
            {/* only show the description if there is a fieldDescription or optionsLengthWarning is set and needs to be shown */}
            {((filteredOptions.length > maxOptionsLength &&
                optionsLengthWarning &&
                !value) ||
                (optionsLengthWarning && value.length < 3) ||
                fieldDescription) && (
                <span className="description">
                    {optionsLengthWarning || fieldDescription}
                </span>
            )}
        </div>
    );
};

export default memo(Dropdown);
